import { Select } from 'antd';
import { type FC, useMemo } from 'react';
import type { SelectProps } from 'antd/lib/select';
import type { SerializedManufacturer, SerializedMeterModel, OutputTechnology } from '@enmon/commons/lib/tools';
import { useMeterModels } from 'api/hooks/meter-models/useMeterModels';
import type { Commodity } from '@enmon/commons/lib/shared';
import { filterOption, filterSort } from '../utils';
import { mapListToIdSelectOptions } from '../../../utils/mapListToIdSelectOptions';
import { isNil } from 'ramda';
import type { MutableEmptyObject } from '@enmon/commons';

type MeterModelId = SerializedMeterModel['id'] | null;

export interface MeterModelsSelectProps extends SelectProps<MeterModelId> {
  filter?: {
    commodity?: Commodity;
    manufacturerId?: SerializedManufacturer['id'];
    technologies?: OutputTechnology[];
    meterModelId?: SerializedMeterModel['id'];
  };
  onChange?: (value: MeterModelId) => void;
  onModelSelect?: (model: SerializedMeterModel | null) => void;
  onSuccess?: () => void;
}

export const MeterModelsSelect: FC<MeterModelsSelectProps> = props => {
  const { filter, onChange, onModelSelect, onSuccess, ...propsToPass } = props;
  const { commodity, manufacturerId, technologies } = filter ?? {};

  const { data: meterModels, isFetching: isLoadingMeterModels } = useMeterModels({
    ...(commodity && { commodity }),
    ...(manufacturerId && { manufacturerId }),
    ...(technologies && { technologies }),
    populate: { manufacturer: { fields: ['id', 'name'] } },
    fields: ['id', 'name', 'manufacturer', 'folder_drive_id'],
    limit: -1,
  });

  const manufacturersOptions = useMemo(() => {
    if (!meterModels?.data) return [];

    const grouped = meterModels.data.reduce<MutableEmptyObject<Record<string, SerializedMeterModel[]>>>(
      (acc, model) => {
        const manufacturerName = model.manufacturer?.name ?? 'Unknown Manufacturer';
        if (!acc[manufacturerName]) {
          acc[manufacturerName] = [];
        }
        acc[manufacturerName].push(model);
        return acc;
      },
      {},
    );

    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, models]) => ({
        label,
        options: mapListToIdSelectOptions(models),
      }));
  }, [meterModels]);

  const handleChange = (value: MeterModelId) => {
    onChange?.(value);
    if (onModelSelect) {
      const model = !isNil(value) ? (meterModels?.data?.find(({ id }) => id === value) ?? null) : null;
      onModelSelect(model);
    }
  };

  return (
    <Select
      allowClear
      showSearch={{
        filterOption,
        filterSort,
        optionFilterProp: 'label',
      }}
      loading={isLoadingMeterModels}
      options={manufacturersOptions}
      placeholder="Select Meter Model"
      popupMatchSelectWidth={false}
      onChange={handleChange}
      {...propsToPass}
    />
  );
};
